import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Alert,
  IconButton,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import CustomDataTable from "../../../components/customTable/CustomDataTable";
import { GridColDef } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../../components/loader/Loader";
import { getFormattedDate } from "../../../utils/commonFunctions";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface AppTrackingData {
  id: string;
  Id: number;
  DateCreated: string;
  EventName: string;
  EventId: string | null;
  EventData: string | null;
  IdentityUserId: string;
  UdId: string;
  Platform: string;
  AppVersion: string;
  OSVersion: string;
  DeviceModel: string;
  AppTags: string;
  ScreenName: string;
  Latitude: number;
  Longitude: number;
  [key: string]: any; // For any additional fields that might be in the response
}

interface AppTrackingPageProps {
  userId: string;
  refreshSignal?: number;
}

const AppTrackingPage: React.FC<AppTrackingPageProps> = ({
  userId,
  refreshSignal
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appTrackingData, setAppTrackingData] = useState<AppTrackingData[]>([]);
  const [selectedEventData, setSelectedEventData] = useState<string | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchAppTrackingData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${apiBaseUrl}/${process.env.REACT_APP_API_APP_TRACKING_URL}/${userId}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      // Transform the data from Activities array to include an id field for the DataGrid
      const activities = response.data?.Activities || [];
      const transformedData = activities.map((item: any, index: number) => ({
        ...item,
        id: item.Id?.toString() || `tracking-${index}`
      }));

      setAppTrackingData(transformedData);
    } catch (err: any) {
      console.error("Failed to fetch app tracking data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch app tracking data"
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAppTrackingData();
  }, [fetchAppTrackingData]);

  const handleRefresh = () => {
    fetchAppTrackingData();
  };

  const handleViewEventData = (eventData: string | null) => {
    setSelectedEventData(eventData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEventData(null);
  };

  const formatEventData = (eventData: string | null): string => {
    if (!eventData) return "No data available";

    try {
      const parsed = JSON.parse(eventData);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return eventData;
    }
  };

  // Define columns for the DataGrid
  const columns: GridColDef[] = [
    {
      field: "DateCreated",
      headerName: "Date Created",
      width: 200,
      valueFormatter: (params) => {
        if (!params.value) return "N/A";
        return getFormattedDate(params.value);
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "EventName",
      headerName: "Event Name",
      width: 200,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "EventId",
      headerName: "Event ID",
      width: 200,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "ScreenName",
      headerName: "Screen Name",
      width: 150,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "Platform",
      headerName: "Platform",
      width: 120,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "DeviceModel",
      headerName: "Device Model",
      width: 150,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "AppVersion",
      headerName: "App Version",
      width: 120,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "OSVersion",
      headerName: "OS Version",
      width: 120,
      valueGetter: (params) => params.value || "N/A",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "location",
      headerName: "Location",
      width: 200,
      valueGetter: (params) => {
        const row = params.row;
        if (row.Latitude && row.Longitude) {
          return `${row.Latitude.toFixed(4)}, ${row.Longitude.toFixed(4)}`;
        }
        return "N/A";
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "EventData",
      headerName: "Event Data",
      width: 300,
      renderCell: (params) => {
        const eventData = params.value;
        if (!eventData) return "N/A";

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewEventData(eventData);
              }}
              sx={{ padding: 0.5 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    }
  ];

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <IconButton onClick={() => handleRefresh()}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {appTrackingData.length === 0 ? (
        <Alert severity="info">No app tracking data found for this user.</Alert>
      ) : (
        <CustomDataTable
          rows={appTrackingData}
          columns={columns}
          getRowId="id"
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      )}

      {/* Event Data Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
              Event Data Details
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              disabled={false}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              fontFamily: "monospace",
              fontSize: "0.875rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "60vh",
              overflow: "auto"
            }}
          >
            {formatEventData(selectedEventData)}
          </Paper>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AppTrackingPage;
